import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Button, Avatar } from '@heroui/react';
import { DataTable, type ColumnMetaExt } from '@/components/common/DataTable';
import { StatusChip } from '@/components/common/StatusChip';
import { formatBytes } from '@/lib/format';
import type { Mailbox } from '@/features/mailboxes/types';
import { EllipsisVerticalIcon, PencilSquareIcon, KeyIcon, TrashIcon, EyeIcon } from '@/icons';

export function MailboxesTable({ mailboxes, isLoading, sort, onSort, onEdit, onReset, onDelete, onOpenInbox, isError, onRetry, emptyMessage = 'No mailboxes match your filters.' }: {
  mailboxes: Mailbox[]; isLoading: boolean; sort: string; onSort?: (f: string) => void;
  onEdit: (m: Mailbox) => void; onReset: (m: Mailbox) => void; onDelete?: (m: Mailbox) => void;
  onOpenInbox?: (m: Mailbox) => void;
  isError?: boolean; onRetry?: () => void; emptyMessage?: string;
}) {
  const navigate = useNavigate();
  const columns = useMemo<ColumnDef<Mailbox, unknown>[]>(() => [
    {
      id: 'address', header: 'Mailbox', meta: { sortKey: 'address' } as ColumnMetaExt,
      cell: ({ row }) => {
        const m = row.original;
        const initials = (m.displayName || m.address).slice(0, 2).toUpperCase();
        return (
          <div className="flex items-center gap-3">
            <Avatar size="sm" name={initials} className="bg-brand/15 text-brand" />
            <div className="min-w-0"><p className="truncate font-medium text-fg">{m.displayName}</p><p className="truncate text-xs text-fg-muted">{m.address}</p></div>
          </div>
        );
      },
    },
    { id: 'status', header: 'Status', cell: ({ row }) => (
      row.original.status === 'suspended' ? <StatusChip tone="danger">Suspended</StatusChip> : <StatusChip tone="success">Active</StatusChip>
    ) },
    {
      id: 'usage', header: 'Storage', meta: { sortKey: 'usedBytes' } as ColumnMetaExt,
      cell: ({ row }) => {
        const m = row.original;
        const pct = m.quotaBytes > 0 ? Math.min(100, (m.usedBytes / m.quotaBytes) * 100) : 0;
        const tone = pct >= 90 ? 'bg-danger' : pct >= 75 ? 'bg-warning' : 'bg-success';
        return (
          <div className="w-40">
            <div className="flex justify-between text-xs text-fg-muted"><span>{formatBytes(m.usedBytes)}</span><span>{formatBytes(m.quotaBytes)}</span></div>
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-surface-raised"><div className={tone} style={{ width: `${pct}%`, height: '100%' }} /></div>
          </div>
        );
      },
    },
    {
      id: 'actions', header: '', meta: { className: 'text-right w-12' } as ColumnMetaExt,
      cell: ({ row }) => (
        // Keeps menu clicks and key presses from also activating the row link.
        <div role="presentation" className="flex justify-end" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          <Dropdown placement="bottom-end">
            <DropdownTrigger><Button isIconOnly size="sm" variant="light" aria-label={`Actions for ${row.original.address}`}><EllipsisVerticalIcon className="h-5 w-5 text-fg-muted" /></Button></DropdownTrigger>
            <DropdownMenu aria-label="Mailbox actions">
              {[
                ...(onOpenInbox ? [<DropdownItem key="open-inbox" startContent={<EyeIcon className="h-4 w-4" />} onPress={() => onOpenInbox(row.original)}>Open inbox (read-only)</DropdownItem>] : []),
                <DropdownItem key="edit" startContent={<PencilSquareIcon className="h-4 w-4" />} onPress={() => onEdit(row.original)}>Edit</DropdownItem>,
                <DropdownItem key="reset" startContent={<KeyIcon className="h-4 w-4" />} onPress={() => onReset(row.original)}>Reset password</DropdownItem>,
                // Delete is offered only when the caller passes onDelete (the user may delete mailboxes).
                ...(onDelete ? [<DropdownItem key="delete" className="text-danger" color="danger" startContent={<TrashIcon className="h-4 w-4" />} onPress={() => onDelete(row.original)}>Delete</DropdownItem>] : []),
              ]}
            </DropdownMenu>
          </Dropdown>
        </div>
      ),
    },
  ], [onEdit, onReset, onDelete, onOpenInbox]);

  return (
    <DataTable<Mailbox> columns={columns} data={mailboxes} getRowId={(m) => m.id} isLoading={isLoading}
      sort={sort} onSort={onSort} onRowClick={(m) => navigate(`/mailboxes/${m.id}`)} emptyMessage={emptyMessage}
      isError={isError} onRetry={onRetry} />
  );
}
